/**
 * Motion primitives.
 *
 * Everything a user can touch animates with a spring, never a CSS transition
 * or a keyframe. The reason is interruptibility: a spring animates from the
 * current on-screen value, so it can be grabbed and redirected mid-flight. A
 * CSS transition animates from a fixed start toward a fixed end and visibly
 * jumps when you re-target it.
 *
 * Springs here are described by bounce + duration rather than
 * mass/stiffness/damping. Duration is not a hard runtime -- a spring has no
 * fixed end -- it is how quickly the value converges.
 */

import type { Transition } from 'motion/react';

/* Presets */

/**
 * Default for anything that did not carry momentum: panels expanding, rows
 * repositioning, a popover opening. Critically damped -- no overshoot.
 *
 * Overshoot on something the user did not throw looks like a bug.
 */
export const springDefault: Transition = {
  type: 'spring',
  bounce: 0,
  duration: 0.35,
};

/**
 * For motion that inherited velocity from a gesture: a flicked task chip, a
 * dragged block released in the air. The bounce is the physical consequence
 * of the throw, which is why it feels right here and wrong above.
 */
export const springMomentum: Transition = {
  type: 'spring',
  bounce: 0.2,
  duration: 0.35,
};

/** Sheets and drawers. Slightly faster, slight overshoot on arrival. */
export const springSheet: Transition = {
  type: 'spring',
  bounce: 0.2,
  duration: 0.3,
};

/** Exits run at ~65% of enter, so dismissal feels responsive. */
export const springExit: Transition = {
  type: 'spring',
  bounce: 0,
  duration: 0.22,
};

/* Reduced motion */

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Degrade a spring to a short cross-fade when the user asked for less motion.
 *
 * Wrap every spring at the call site. Reduced motion means no travel and no
 * overshoot -- it does not mean no feedback, so the opacity change survives.
 */
export function motionSafe(options: Transition): Transition {
  if (!prefersReducedMotion()) return options;
  return { duration: 0.15, ease: 'easeOut' };
}

/** Transform properties to drop entirely under reduced motion. */
export function motionSafeTransform<T extends Record<string, unknown>>(
  transform: T,
  fallback: Partial<T> = {},
): Partial<T> {
  return prefersReducedMotion() ? fallback : transform;
}

/* Velocity tracking */

interface Sample {
  x: number;
  y: number;
  t: number;
}

/**
 * Rolling pointer history.
 *
 * Velocity is measured across a short window rather than between the last two
 * events. Two-event deltas are dominated by whatever jitter happened in the
 * final 4ms, which makes a smooth flick land somewhere unpredictable.
 */
export class VelocityTracker {
  private samples: Sample[] = [];
  private readonly windowMs: number;

  constructor(windowMs = 100) {
    this.windowMs = windowMs;
  }

  add(x: number, y: number, t: number = performance.now()): void {
    this.samples.push({ x, y, t });
    const cutoff = t - this.windowMs;
    while (this.samples.length > 2 && this.samples[0]!.t < cutoff) {
      this.samples.shift();
    }
  }

  /** Pixels per second. */
  velocity(): { vx: number; vy: number } {
    if (this.samples.length < 2) return { vx: 0, vy: 0 };

    const first = this.samples[0]!;
    const last = this.samples[this.samples.length - 1]!;
    const dt = (last.t - first.t) / 1000;

    if (dt <= 0) return { vx: 0, vy: 0 };
    return { vx: (last.x - first.x) / dt, vy: (last.y - first.y) / dt };
  }

  reset(): void {
    this.samples = [];
  }
}

/* Momentum projection */

/**
 * Where a flick would come to rest, given its release velocity.
 *
 * Snap to the target nearest the *projected* endpoint, not nearest the release
 * point. That difference is the whole reason a flick feels like a throw rather
 * than a nudge -- a small input producing a large, predictable output.
 *
 * This is exponential decay, matching platform scroll deceleration. The
 * textbook v^2/(2a) form is not what native scrolling does and feels wrong
 * next to it.
 *
 * @param velocity px/s at release
 * @param decelerationRate 0.998 normal scroll feel; 0.99 snappier
 */
export function projectMomentum(velocity: number, decelerationRate = 0.998): number {
  return ((velocity / 1000) * decelerationRate) / (1 - decelerationRate);
}

/** Nearest value in `points` to `value`. */
export function nearestSnapPoint(value: number, points: readonly number[]): number {
  if (points.length === 0) return value;
  return points.reduce((best, p) =>
    Math.abs(p - value) < Math.abs(best - value) ? p : best,
  );
}

/* Boundaries */

/**
 * Progressive resistance past an edge, instead of a hard stop.
 *
 * A hard stop reads as frozen -- the user checks whether the app hung. Falling
 * resistance reads as responsive, with nothing more to reach.
 *
 * @param overshoot how far past the boundary the pointer has gone
 * @param dimension size of the container along that axis
 */
export function rubberband(overshoot: number, dimension: number, constant = 0.55): number {
  if (dimension <= 0) return 0;
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
}

/* Interruption */

/**
 * Live on-screen transform of an element, mid-animation.
 *
 * When a gesture interrupts a running animation, the new animation must start
 * from what is actually rendered, not from the logical target. Starting from
 * the target produces a visible jump, which is the most common tell of an
 * amateur implementation.
 */
export function currentTransform(el: HTMLElement): { x: number; y: number; scale: number } {
  const style = window.getComputedStyle(el);
  const transform = style.transform;

  if (!transform || transform === 'none') return { x: 0, y: 0, scale: 1 };

  const matrix = new DOMMatrixReadOnly(transform);
  return {
    x: matrix.m41,
    y: matrix.m42,
    // Uniform scale assumed; we never animate x and y scale independently.
    scale: Math.hypot(matrix.m11, matrix.m12),
  };
}

/* Gesture constants */

/** Movement before a pointer-down commits to a drag, so a tap never becomes one. */
export const DRAG_THRESHOLD_PX = 10;

/** Calendar drop granularity. */
export const SNAP_MINUTES = 15;

/** Chip lift while dragging. Small: the chip should read as picked up, not launched. */
export const DRAG_LIFT_SCALE = 1.03;
